/**
 * CascadeView - top-level route for /twin/cascade (P2.6.e).
 *
 * The user-facing surface for "what's degraded right now":
 *   1. Banner -- "N products impacted" pulling product_count directly
 *      from the API response (no client-side aggregation).
 *   2. Impact cards -- one per CascadeImpactOut, in API order.
 *   3. Products impacted -- footer list of downstream products with
 *      the IBSes that brought them into scope.
 *
 * Polling is 15s via the useCascade hook (CASCADE_POLL_MS); this
 * component just renders whatever the hook returns. Loading + error +
 * empty branches all explicit so the operator never sees a blank
 * page on a transient fetch failure.
 *
 * Module-boundary rule: this file imports from `twin/*` and
 * `shared/*` only. eslint enforces.
 */
import { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Explainer } from "shared/components/Explainer";
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { AsOfBanner } from "shared/components/AsOfBanner";
import { AsOfPicker } from "shared/components/AsOfPicker";
import { useAsOf } from "shared/hooks/useAsOf";
import { FullscreenToggle, useFullscreen } from "shared/layout/FullscreenToggle";
import { formatSAR, isMoneyNotMeasured } from "shared/money/money";
import { useCascade } from "twin/api/hooks";
import { CascadeBanner } from "twin/cascade/CascadeBanner";
import { CascadeImpactCard } from "twin/cascade/CascadeImpactCard";
import { SinceLastLooked } from "twin/whatsnew/SinceLastLooked";
import { useLiveTolerance } from "shared/tolerance/useLiveTolerance";
import { Twin3DStage, Twin3DToggle, useTwin3DMode } from "twin/twin3d/Twin3DMode";

export function CascadeView() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const asOf = useAsOf();
  const cascade = useCascade(asOf ? { as_of: asOf } : {});
  const live = useLiveTolerance();
  const breachingSince = new Map(
    (live.data?.rows ?? [])
      .filter((r) => r.verdict === "breached" && r.breach_since)
      .map((r) => [r.ibs_service_id, r.breach_since as string]),
  );
  const rootRef = useRef<HTMLElement | null>(null);
  const { isFullscreen } = useFullscreen(rootRef);
  // Brief two, section 1: flipping to 3D on this tab lands on the
  // CASCADE view, not the constellation.
  const { effectiveMode, setMode, onWebglFail, loadFailed } = useTwin3DMode("cascade");

  // The 3D stage fetches its own graph, so it must not sit behind the
  // 2D read's loading and error branches -- and the toggle has to stay
  // reachable from inside 3D, or the operator is stranded there.
  if (effectiveMode === "3d") {
    return (
      <article ref={rootRef} className="flex flex-col gap-3">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">
              <MarsadIcon
                name="cascade"
                size={20}
                className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
              />
              {t("modules.twin.cascade.heading")}
            </h1>
            <p className="mt-1 max-w-prose text-sm text-slate-600">
              {t("modules.twin.cascade.subheading")}
            </p>
          </div>
          <Twin3DToggle
            mode={effectiveMode}
            onChange={setMode}
            testid="cascade-view"
            loadFailed={loadFailed}
          />
        </header>
        <Twin3DStage view="cascade" onWebglFail={onWebglFail} />
      </article>
    );
  }

  if (cascade.isLoading) {
    return (
      <div data-testid="cascade-loading" className="text-sm text-slate-500">
        {t("modules.twin.cascade.loading")}
      </div>
    );
  }

  if (cascade.isError) {
    return (
      <article className="max-w-3xl">
        <h1 className="text-2xl font-semibold mb-3">
          <MarsadIcon
            name="cascade"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("modules.twin.cascade.heading")}
        </h1>
        <p
          data-testid="cascade-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-4"
        >
          <strong className="block font-semibold mb-1">
            {t("modules.twin.cascade.errorTitle")}
          </strong>
          {cascade.error.detail}
        </p>
      </article>
    );
  }

  const data = cascade.data;
  // C3 -- the board's RED chip was inert. It now opens this page
  // filtered to the severity it counts, so the figure and the rows
  // are the same set rather than two views a reader has to reconcile.
  const severityFilter = searchParams.get("severity");
  // D2 (round 8) -- and the same for the board's "10 / 16 IBSes".
  // That figure counts IBSes; this page counts every entity a finding
  // reaches, which on this estate is 13. Both are honest and they look
  // like a contradiction, so the tile now opens the page filtered to
  // what it counted -- the figure and the rows are one set.
  const kindFilter = searchParams.get("kind");
  // And the board's per-outcome RAG tile, which counts the IBSes in
  // one outcome.
  const outcomeFilter = searchParams.get("outcome");
  const shown = (data?.impacted ?? []).filter(
    (i) =>
      (!severityFilter || i.headline_finding.severity === severityFilter) &&
      (!kindFilter || i.entity_kind === kindFilter) &&
      (!outcomeFilter || i.outcome_category === outcomeFilter),
  );
  if (!data) {
    // Should not be reachable: isLoading was false and isError was
    // false, so data must be defined. Defensive branch keeps the
    // type narrowing explicit.
    return null;
  }

  return (
    <article
      ref={rootRef}
      className={isFullscreen ? "min-h-screen w-full bg-white p-6" : "max-w-5xl"}
    >
      <header className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">
            <MarsadIcon
              name="cascade"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("modules.twin.cascade.heading")}
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-prose">
            {t("modules.twin.cascade.subheading")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Twin3DToggle
            mode={effectiveMode}
            onChange={setMode}
            testid="cascade-view"
            loadFailed={loadFailed}
          />
          <FullscreenToggle targetRef={rootRef} />
        </div>
      </header>

      <Explainer termKey="cascade" className="max-w-3xl" />

      <AsOfPicker />

      {/* D3 -- the picker rewinds the sealed chains, not the findings.
          Showing a date and letting a reader believe the whole page
          moved to it is the difference between a limitation and a
          lie. */}
      <AsOfBanner asOf={asOf} />

      <CascadeBanner summary={data} />

      {/* Round 20 U7 -- the cards are ordered by entity; what is new
          since the reader's last look is listed apart, newest first. */}
      <SinceLastLooked surface="cascade" />

      {shown.length === 0 ? (
        /* Round 12 B1 -- "All clear. No open critical or high findings
           impact any service right now" over a tenant with no admitted
           services, no declared tolerances and no telemetry. True, and
           a claim about an estate nobody had looked at. Zero impacted
           because nothing is wrong and zero impacted because there is
           nothing there are the same zero, so the server says which.
           Slate, not emerald: the absence of a verdict is not a
           quieter version of a good one. */
        <section
          data-testid="cascade-empty"
          data-assessed={data?.estate_assessed === false ? "false" : "true"}
          className={
            data?.estate_assessed === false
              ? "rounded-lg border border-slate-200 bg-slate-50 px-5 py-6 text-slate-700"
              : "rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-6 text-emerald-900"
          }
        >
          <h2 className="text-lg font-semibold mb-1">
            {t(
              data?.estate_assessed === false
                ? "modules.twin.cascade.notAssessedTitle"
                : "modules.twin.cascade.emptyTitle",
            )}
          </h2>
          <p className="text-sm">
            {t(
              data?.estate_assessed === false
                ? "modules.twin.cascade.notAssessedBody"
                : "modules.twin.cascade.emptyBody",
            )}
          </p>
        </section>
      ) : (
        <section
          data-testid="cascade-impact-list"
          className="grid gap-3 md:grid-cols-2"
        >
          {/* C3 -- second round. The board's "10 / 15" opened a bare
              cascade whose ten cards WERE the ten, unlabelled, under a
              heading counting something else. A reader arriving from a
              figure needs the page to say that these are it. */}
          <p
            data-testid="cascade-impact-strip"
            className="md:col-span-2 text-sm text-slate-600"
          >
            {kindFilter
              ? t("modules.twin.cascade.strip.kind", {
                  count: shown.length,
                  // Round 9 E7 -- the vocabulary, not the column. A
                  // regex swapping the underscore for a space reads as
                  // a word and is still the enum: "ibs services".
                  // No casing transform here: the catalogue carries the
                  // mid-sentence form, because "IBS" downcased is wrong
                  // and "Service" left capitalised mid-sentence is too.
                  kind: t(
                    `modules.twin.entity.${
                      shown.length === 1 ? "kindInSentence" : "kindInSentencePlural"
                    }.${kindFilter}`,
                  ),
                })
              : severityFilter
                ? t("modules.twin.cascade.strip.filtered", {
                    count: shown.length,
                    severity: severityFilter,
                  })
                : t("modules.twin.cascade.strip.all", { count: shown.length })}
            {(severityFilter || kindFilter || outcomeFilter) && (
              <span data-testid="cascade-strip-summary-note" className="ms-1">
                {t("modules.twin.cascade.strip.summaryUnfiltered")}
              </span>
            )}
          </p>
          {shown.map((impact) => (
            <CascadeImpactCard
              key={`${impact.entity_kind}:${impact.entity_id}`}
              impact={impact}
              asOf={asOf}
              severities={data.severities}
              breachingSince={
                impact.entity_kind === "ibs_service"
                  ? (breachingSince.get(impact.entity_id) ?? null)
                  : null
              }
            />
          ))}
        </section>
      )}

      {data.impacted_products.length > 0 && (
        <section
          data-testid="cascade-products"
          className="mt-6 rounded-lg border border-slate-200 bg-white p-4"
        >
          <h2 className="text-base font-semibold text-slate-900 mb-2">
            {t("modules.twin.cascade.products.heading")}
          </h2>
          <ul className="text-sm text-slate-700 space-y-1">
            {data.impacted_products.map((product) => {
              // P14.1 (#478): per-product SAR line. Measured ->
              // "SAR 1.85M annual / SAR 5,068 daily"; unmeasured ->
              // an italicised "Revenue not measured" so the row
              // explicitly *says* it's not zero, just unknown.
              const annual = formatSAR(product.annual_revenue_at_risk);
              const daily = formatSAR(product.daily_revenue_at_risk);
              const measured = !isMoneyNotMeasured(annual);
              return (
                <li
                  key={product.product_id}
                  data-testid="cascade-product"
                  className="flex flex-wrap items-baseline gap-x-3"
                >
                  <span className="font-medium">{product.product_name}</span>
                  <span className="text-xs text-slate-500">{product.product_code}</span>
                  <span className="text-xs text-slate-500">
                    {t("modules.twin.cascade.products.viaIbs", {
                      count: product.impacted_via_ibs_ids.length,
                    })}
                  </span>
                  {measured ? (
                    <span
                      data-testid="cascade-product-revenue"
                      className="text-xs tabular-nums text-slate-700"
                    >
                      {t("modules.twin.cascade.products.revenueLine", {
                        annual,
                        daily,
                      })}
                    </span>
                  ) : (
                    <span
                      data-testid="cascade-product-revenue-unmeasured"
                      className="text-xs italic text-slate-400"
                    >
                      {t("modules.twin.cascade.products.revenueUnmeasured")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </article>
  );
}
